/* eslint-disable max-lines -- I can't find any logical way to split this file */
import { Injectable, Injector } from '@angular/core';
import { Store } from '@sama/app-state';
import { Deferred } from '@sama/js-core';
import { Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { toolNameForEventTracking } from '../../../../cross-team-shared/annotation/shared/abstract-tool';
import { ShapeState2D } from '../../../../cross-team-shared/annotation/shared/state/shape-state';
import {
  InteractiveBorderEventInfo,
  MouseEventInfo,
  ShapeEventInfo,
  SubpointEventInfo,
} from '../../../../cross-team-shared/misc-services/events/mouse-event.service';
import { KeyboardShortcutService } from '../../../../cross-team-shared/misc-services/keyboard-shortcut.service';
import { EventTrackingValueWithSource } from '../../../../cross-team-shared/misc-utils';
import { VectorLayerEditMode } from '../../../../cross-team-shared/types/tool-modes';
import { TestHookService } from '../../../../shared/misc-services/test-hook.service';
import { VectorTaggingShapeType } from '../../../../team-apis/otterdam/answers/shape-typings';
import { getShapeHelper } from '../../shape-helpers/get-shape-helper';
import { AbstractSensorContentStore2D } from '../../state/abstract-sensor-content-store-2d';
import { InspectService } from '../../toolbars/inspect.service';
import { RectangleExpandContractService } from '../../utils/rectangle-expand-contract.service';
import { ShapeTranslationService } from '../../utils/shape-translation.service';
import { SliderRectangleSliderTranslationService } from '../../utils/slider-rectangle-slider-translation.service';
import { SelectTool2D } from '../select/select-tool-2d';
import { MultipleSubpointTranslationHelper } from '../shared/drag-handlers/multiple-subpoint-translation-helper';
import { VectorDragHandler } from '../shared/drag-handlers/vector-drag-handler';
import { AddRemoveModeHelperService } from './add-remove-mode-helper.service';
import { AddedSubpointTranslationHelper } from './added-subpoint-translation-helper';
import { EditSecondaryToolbar2DComponent } from './edit-secondary-toolbar-2d.component';
import { RemoveSubpointDragHandler } from './remove-subpoint-drag-handler';
import { ShapeTranslationHelper } from './shape-translation-helper';

export const addSubpointHook = Symbol();

type EditModeFallback = 'drag' | 'clicky' | 'add';

@Injectable()
export class EditTool2D extends SelectTool2D {
  override readonly name = 'Edit';
  editMode$: Observable<VectorLayerEditMode>;
  override secondaryToolbarType = EditSecondaryToolbar2DComponent;

  private editModeFallback: EditModeFallback = 'drag';
  private removeSubpointDragHandler?: RemoveSubpointDragHandler;
  private previousMode?: VectorLayerEditMode;

  constructor(
    private addRemoveModeHelperService: AddRemoveModeHelperService,
    private inspectService: InspectService,
    private keyboardShortcutService: KeyboardShortcutService,
    private shapeTranslationService: ShapeTranslationService,
    private rectangleExpandContractService: RectangleExpandContractService,
    private sliderRectangleSliderTranslationService: SliderRectangleSliderTranslationService,
    private testHookService: TestHookService,
    injector: Injector,
    store: AbstractSensorContentStore2D,
  ) {
    super(injector);
    this.subscribeTo(
      this.addRemoveModeHelperService.subpointAdded$,
      this.startDragOfAddedSubpoint,
    );
    this.editMode$ = store('editMode').$.pipe(distinctUntilChanged());
  }

  override async onActivate() {
    await super.onActivate();

    this.setEditMode(this.editModeFallback);

    if (this.addRemoveModeHelperService.allowIncrementalShapeToolModes()) {
      this.addRemoveModeHelperService.setUpSubscriptions();
      this.setUpAddSubscription();
      this.setUpRemoveSubscription();
    }

    this.onEvent('interactiveBorder', 'mousedown', this.onBorderMouseDown);

    this.setUpMovementKeyboardShortcuts();
    // For remove mode
    this.onEvent('container', 'mousedown', this.onContainerMouseDown, {
      modifiers: ['altKey'],
    });
  }

  override onDeactivate() {
    super.onDeactivate();
    this.addRemoveModeHelperService.unsubscribe();
    this.shapeTranslationService.unsubscribe();
    this.rectangleExpandContractService.unsubscribe();
    this.sliderRectangleSliderTranslationService.unsubscribe();
    this.setEditMode('none');
    this.previousMode = undefined;
  }

  setEditModeFromUser(
    mode: VectorLayerEditMode,
    eventTrackingValue?: EventTrackingValueWithSource,
  ) {
    if (mode === 'drag' || mode === 'clicky' || mode === 'add') {
      this.editModeFallback = mode;
    }
    this.setEditMode(mode);

    // bug: this fires the event even when mid-drag in add mode
    if (eventTrackingValue) {
      this.shellConnector.trackEvent({
        id: 'change_tool_mode',
        bucket: 'adjustment',
        ...eventTrackingValue,
        mode,
        tool: toolNameForEventTracking(this.name),
      });
    }
  }

  override setMode(mode: string) {
    if (mode === 'add' || mode === 'remove') {
      this.setEditModeFromUser(mode);
    } else {
      this.setEditModeFromUser('drag');
    }
  }

  protected override onContainerMouseDown(
    event: MouseEvent,
    eventInfo: MouseEventInfo,
  ) {
    if (this.isInDragMode() || this.isInClickyMode()) {
      super.onContainerMouseDown(event, eventInfo, 'edit_tool');
    } else if (this.isInRemoveMode()) {
      const dragHandler = new RemoveSubpointDragHandler(event, this.injector);
      dragHandler.drag();
      this.registerHelper(dragHandler);
      this.removeSubpointDragHandler = dragHandler;
    }
  }

  protected override onShapeMouseDown(
    event: MouseEvent,
    eventInfo: ShapeEventInfo,
  ) {
    if (
      !this.isInDragMode() &&
      !this.isInClickyMode() &&
      !this.addRemoveModeHelperService.canSelectShape(event)
    ) {
      return;
    }

    if (this.isInDragMode()) {
      const dragHandler = new VectorDragHandler(
        event,
        this.injector,
        new ShapeTranslationHelper(this.injector, {
          clickedShapeStore: eventInfo.shapeStore,
        }),
      );
      this.inspectService.pauseUntil(dragHandler.completion);
      this.registerHelper(dragHandler);
    }
    super.onShapeMouseDown(event, eventInfo, 'edit_tool');
  }

  protected override onShapeDoubleClick(
    event: MouseEvent,
    eventInfo: ShapeEventInfo,
  ) {
    if (this.isInDragMode() || this.isInClickyMode()) {
      super.onShapeDoubleClick(event, eventInfo);
    }
  }

  protected override allowsSubpointSelection(
    shapeType: VectorTaggingShapeType,
  ) {
    return !this.isInClickyMode() && super.allowsSubpointSelection(shapeType);
  }

  protected override onSubpointMouseDown(
    event: MouseEvent,
    eventInfo: SubpointEventInfo,
  ) {
    if (!this.isDraggableSubpoint(eventInfo)) {
      return;
    }

    // If you unselect a subpoint and drag at the same time the drag handler has
    // to be created after the super call which calls the selection service.
    const dragDeferred = new Deferred<void>();
    this.inspectService.pauseUntil(dragDeferred.promise);
    super.onSubpointMouseDown(event, eventInfo, 'edit_tool');

    if (
      getShapeHelper(
        eventInfo.shapeStore.state().type,
      ).isSubpointDraggingEnabled(this.store.state().editMode)
    ) {
      const dragHandler = this.getSubpointDragHandler(event, eventInfo);
      this.registerHelper(dragHandler);
      void dragHandler.completion.finally(() => {
        dragDeferred.resolve();
      });
    } else {
      dragDeferred.resolve();
    }
  }

  protected override setSelectedTool() {
    this.shellConnector.setSelectedTool(this.name, this.store.state().editMode);
  }

  private isDraggableSubpoint(eventInfo: SubpointEventInfo) {
    if (this.addRemoveModeHelperService.canDragSubpoint(eventInfo)) {
      return true;
    }

    return !(this.isInAddMode() || this.isInRemoveMode());
  }

  private isInAddMode() {
    return this.store.state().editMode === 'add';
  }

  private isInRemoveMode() {
    return this.store.state().editMode === 'remove';
  }

  private isInClickyMode() {
    return this.store.state().editMode === 'clicky';
  }

  private isInDragMode() {
    return this.store.state().editMode === 'drag';
  }

  private setUpRemoveSubscription() {
    this.subscribeToWhileActive(
      this.getMatchingKeyboardShortcutEvent$('altDown'),
      ({ eventTrackingValue }) => {
        this.previousMode = this.store.state().editMode;
        this.setEditModeFromUser('remove', eventTrackingValue);
      },
    );

    this.subscribeToWhileActive(
      this.getMatchingKeyboardShortcutEvent$('altUp'),
      ({ eventTrackingValue }) => {
        void this.removeSubpointDragHandler?.stopListening();
        this.previousMode &&
          this.setEditModeFromUser(this.previousMode, eventTrackingValue);
      },
    );
  }

  private setUpAddSubscription() {
    this.subscribeToWhileActive(
      this.getMatchingKeyboardShortcutEvent$('shiftDown'),
      ({ eventTrackingValue }) => {
        this.previousMode = this.store.state().editMode;
        this.setEditModeFromUser('add', eventTrackingValue);
      },
    );

    this.subscribeToWhileActive(
      this.getMatchingKeyboardShortcutEvent$('shiftUp'),
      ({ eventTrackingValue }) => {
        this.previousMode &&
          this.setEditModeFromUser(this.previousMode, eventTrackingValue);
      },
    );
  }

  private getMatchingKeyboardShortcutEvent$(shortcutName: string) {
    return this.keyboardShortcutService.getMatchingEvent$(shortcutName);
  }

  private onBorderMouseDown(
    event: MouseEvent,
    eventInfo: InteractiveBorderEventInfo,
  ) {
    this.lastMouseDownEventFromShapeOrShapePart = event;
    const dragHandler = getShapeHelper(
      eventInfo.shapeStore.state().type,
    ).getInteractiveBorderDragHandler(eventInfo, this.injector);
    this.registerHelper(dragHandler);
    this.inspectService.pauseUntil(dragHandler.completion);
    this.modifySelection(eventInfo.shapeStore, event, 'edit_tool');
  }

  private startDragOfAddedSubpoint(eventInfo: SubpointEventInfo) {
    const dragHandler = new VectorDragHandler(
      eventInfo.event,
      this.injector,
      new AddedSubpointTranslationHelper(
        eventInfo.shapeStore,
        eventInfo.subpointIndex,
        this.injector,
      ),
      { ignoreNonDrags: false },
    );
    dragHandler.startBatchEvent();

    this.testHookService.run(addSubpointHook);

    this.modifySelection(eventInfo.shapeStore, eventInfo.event, 'edit_tool');
    this.setEditMode('drag');
    this.registerHelper(dragHandler);

    dragHandler.endBatchEvent();
  }

  private getSubpointDragHandler(
    event: MouseEvent,
    eventInfo: SubpointEventInfo,
  ) {
    if (
      this.isSubpointSelected(eventInfo.shapeStore, eventInfo.subpointIndex) &&
      this.store.state().editMode !== 'clicky'
    ) {
      return new VectorDragHandler(
        event,
        this.injector,
        new MultipleSubpointTranslationHelper(this.injector),
        {
          restrictInteractions: true,
        },
      );
    } else {
      return getShapeHelper(
        eventInfo.shapeStore.state().type,
      ).getSingleSubpointDragHandler(
        eventInfo,
        this.store.state().editMode,
        this.injector,
      );
    }
  }

  private isSubpointSelected(shapeStore: Store<ShapeState2D>, index: number) {
    const shapeSelection =
      this.store.state().selection[shapeStore.state().index];
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    return shapeSelection ? shapeSelection[index] : false;
  }

  private setEditMode(mode: VectorLayerEditMode) {
    this.store.batch(() => {
      if (mode === 'clicky') {
        this.subpointSelectionService.clearSubpointSelection();
      }
      this.store('editMode').set(mode);
    });
    this.setSelectedTool();
  }

  private setUpMovementKeyboardShortcuts() {
    this.shapeTranslationService.setUpKeyboardShortcuts();
    this.rectangleExpandContractService.setUpKeyboardShortcuts();
    this.sliderRectangleSliderTranslationService.setUpKeyboardShortcuts();
  }
}
